import { FC } from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { getActiveNavigationItemIndex } from "./NavigationList.utilities";

import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import NavigationItem from "./components/NavigationItem";

import { brandColorTokens } from "@constants";
import { navigationSections } from "../../Drawer.constants";

import { INavigationListProperties } from "./NavigationList.types";

import { getActiveIndicatorStyle } from "./NavigationList.styles";

const NavigationList: FC<INavigationListProperties> = ({ isCollapsed, onNavigate }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  return (
    <Box
      component="nav"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        flexGrow: 1,
        overflowX: "hidden",
        overflowY: "auto",
        pt: 3,
        pb: 2,
      }}
    >
      {navigationSections.map((section, sectionIndex) => {
        const activeIndex = getActiveNavigationItemIndex(section.items, pathname);

        return (
          <Box key={section.titleKey}>
            {sectionIndex > 0 && <Divider sx={{ mx: 3, mb: 3, borderColor: brandColorTokens.line.light }} />}
            <Typography
              component="h2"
              variant="caption"
              sx={{
                display: "block",
                color: alpha(brandColorTokens.text.bodyInverted, 0.7),
                fontWeight: 500,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                lineHeight: 1,
                px: 3,
                pb: 1.5,
                whiteSpace: "nowrap",
                opacity: isCollapsed ? 0 : 1,
                transition: "opacity 0.2s",
              }}
            >
              {t(section.titleKey)}
            </Typography>
            <List disablePadding={true} sx={{ position: "relative" }}>
              {activeIndex >= 0 && (
                <Box
                  component="li"
                  aria-hidden={true}
                  data-cy="app-layout-navigation-indicator"
                  sx={getActiveIndicatorStyle(activeIndex)}
                />
              )}
              {section.items.map((item, index) => (
                <NavigationItem
                  key={item.itemKey}
                  item={item}
                  isActive={index === activeIndex}
                  isCollapsed={isCollapsed}
                  onNavigate={onNavigate}
                />
              ))}
            </List>
          </Box>
        );
      })}
    </Box>
  );
};

export default NavigationList;
